/*
 * La aplicación: rail + una pantalla, con la paleta, los formularios, el botón de
 * captura y los avisos montados una sola vez encima de todo.
 *
 * Rutas, iguales a las del prototipo del cliente:
 *   /hoy                          la cola de trabajo (default)
 *   /cuentas                      tabla y tablero
 *   /cuentas/:id/:pestana?        el espacio de trabajo de una cuenta
 *   /descubrimiento               tablero D0–D6 de todas las cuentas
 *   /verificacion/:caso           la cola de hallazgos, pantalla completa
 *   /proyectos · /proyectos/:id/:pestana   lo que pasa después de la propuesta
 *   /cxc                          cobros de todas las cuentas
 *   /notificaciones               activar avisos de cambios del equipo (fuera del rail, por ⌘K)
 */

import { useCallback, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";

import { sesion } from "./api/cliente";
import { refrescarSuscripcion } from "./api/push";
import { CxC } from "./pantallas/CxC";
import { Cuenta } from "./pantallas/cuenta/Cuenta";
import { Cuentas } from "./pantallas/Cuentas";
import { Descubrimiento } from "./pantallas/Descubrimiento";
import { Hoy } from "./pantallas/Hoy";
import { Notificaciones } from "./pantallas/Notificaciones";
import { Proyecto } from "./pantallas/Proyecto";
import { Proyectos } from "./pantallas/Proyectos";
import { Verificacion } from "./pantallas/Verificacion";
import { Acceso } from "./shell/Acceso";
import { ProveedorAvisos } from "./shell/Aviso";
import { ProveedorFormularios, useFormularios } from "./shell/Formularios";
import { Paleta } from "./shell/Paleta";
import { Rail } from "./shell/Rail";
import { Icono, Kbd } from "./ui/primitivas";

export default function App() {
  const [dentro, setDentro] = useState(() => sesion.token() !== "");
  if (!dentro) return <Acceso onEntrar={() => setDentro(true)} />;
  return (
    <ProveedorAvisos>
      <ProveedorFormularios>
        <Shell salir={() => { sesion.salir(); setDentro(false); }} />
      </ProveedorFormularios>
    </ProveedorAvisos>
  );
}

function Shell({ salir }: { salir: () => void }) {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const { abrir } = useFormularios();
  const [expandido, setExpandido] = useState(false);
  const [paleta, setPaleta] = useState(false);
  const [atajos, setAtajos] = useState(false);
  const movil = useMovil();
  const [menu, setMenu] = useState(false);
  const enVerificacion = pathname.startsWith("/verificacion");

  // En el teléfono el rail es un cajón: se cierra al llegar a otra pantalla.
  useEffect(() => setMenu(false), [pathname]);

  // Si este dispositivo ya tiene avisos, se vuelve a registrar: el navegador puede
  // renovar la suscripción, o quien entra puede ser otra persona.
  useEffect(() => { refrescarSuscripcion().catch(() => {}); }, []);

  // Si la API dice 401 o la sesión vence, cliente.ts la borra; aquí lo notamos y volvemos a la entrada.
  useEffect(() => {
    const t = window.setInterval(() => { if (sesion.token() === "") salir(); }, 2000);
    return () => window.clearInterval(t);
  }, [salir]);

  const capturar = useCallback((que: string) => {
    const m = pathname.match(/^\/cuentas\/([^/]+)/);
    const cuentaId = m?.[1];
    if (que === "cuenta") abrir({ tipo: "cuenta" });
    else abrir({ tipo: "interaccion", cuentaId });
  }, [abrir, pathname]);

  useEffect(() => {
    let g = false, gTimer = 0;
    const h = (e: KeyboardEvent) => {
      const el = document.activeElement;
      const enCampo = !!el && /INPUT|TEXTAREA|SELECT/.test(el.tagName);
      const k = e.key.toLowerCase();
      if ((e.metaKey || e.ctrlKey) && k === "k") { e.preventDefault(); setPaleta((p) => !p); return; }
      if ((e.metaKey || e.ctrlKey) && e.key === "\\") { e.preventDefault(); setExpandido((x) => !x); return; }
      if (enCampo || paleta || document.querySelector(".velo")) return;
      if (g) {
        g = false; window.clearTimeout(gTimer);
        const destino = { h: "/hoy", c: "/cuentas", d: "/descubrimiento", p: "/proyectos", m: "/cxc" }[k];
        if (destino) { e.preventDefault(); nav(destino); return; }
      }
      if (k === "g") { g = true; gTimer = window.setTimeout(() => { g = false; }, 800); return; }
      if (k === "c" && !enVerificacion) { e.preventDefault(); capturar("interaccion"); return; }
      if (e.key === "?" && !enVerificacion) { e.preventDefault(); setAtajos((s) => !s); return; }
      if (e.key === "Escape") setAtajos(false);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [nav, paleta, enVerificacion, capturar]);

  return (
    <div style={{ display: "flex", flexDirection: movil ? "column" : "row", height: "100%", overflow: "hidden" }}>
      {!movil && <Rail expandido={expandido} setExpandido={setExpandido} abrirPaleta={() => setPaleta(true)} salir={salir} />}
      {movil && <BarraMovil abrir={() => setMenu(true)} />}
      {movil && menu && (
        <>
          <div onClick={() => setMenu(false)} style={{ position: "fixed", inset: 0, background: "rgba(11,29,58,.35)", zIndex: 110 }} />
          <div style={{ position: "fixed", top: 0, bottom: 0, left: 0, zIndex: 111, display: "flex" }}>
            <Rail expandido setExpandido={() => {}} abrirPaleta={() => { setMenu(false); setPaleta(true); }} salir={salir} />
          </div>
        </>
      )}
      <main style={{ flex: 1, minWidth: 0, minHeight: 0, height: movil ? undefined : "100%", overflowY: enVerificacion ? "hidden" : "auto" }}>
        <Routes>
          <Route path="/" element={<Navigate to="/hoy" replace />} />
          <Route path="/hoy" element={<Hoy />} />
          <Route path="/cuentas" element={<Cuentas />} />
          <Route path="/cuentas/:id" element={<Cuenta />} />
          <Route path="/cuentas/:id/:pestana" element={<Cuenta />} />
          <Route path="/descubrimiento" element={<Descubrimiento />} />
          <Route path="/verificacion/:caso" element={<Verificacion />} />
          <Route path="/proyectos" element={<Proyectos />} />
          <Route path="/proyectos/:id" element={<Proyecto />} />
          <Route path="/proyectos/:id/:pestana" element={<Proyecto />} />
          <Route path="/cxc" element={<CxC />} />
          <Route path="/notificaciones" element={<Notificaciones />} />
          <Route path="*" element={<Navigate to="/hoy" replace />} />
        </Routes>
      </main>
      {!enVerificacion && <BotonCapturar capturar={capturar} />}
      <Paleta abierta={paleta} cerrar={() => setPaleta(false)} capturar={capturar} />
      {atajos && <HojaAtajos cerrar={() => setAtajos(false)} />}
    </div>
  );
}

function useMovil(): boolean {
  const consulta = "(max-width: 768px)";
  const [movil, setMovil] = useState(() => window.matchMedia(consulta).matches);
  useEffect(() => {
    const mq = window.matchMedia(consulta);
    const h = () => setMovil(mq.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  return movil;
}

// En el teléfono el rail no cabe: una barra con el botón de tres líneas lo abre como cajón.
function BarraMovil({ abrir }: { abrir: () => void }) {
  return (
    <div style={{ height: 52, flexShrink: 0, display: "flex", alignItems: "center", gap: 10, padding: "0 8px", background: "var(--navy)" }}>
      <button onClick={abrir} aria-label="Abrir menú" title="Menú"
        style={{ width: 40, height: 40, display: "grid", placeItems: "center", color: "#fff", borderRadius: "var(--r-control)" }}>
        <Icono d="M3 5h14M3 10h14M3 15h14" size={22} />
      </button>
      <img src="/assets/nexlum_wordmark_reversed.png" alt="Nexlum" style={{ height: 18, width: "auto" }} />
    </div>
  );
}

function BotonCapturar({ capturar }: { capturar: (que: string) => void }) {
  const [abierto, setAbierto] = useState(false);
  const opciones: [string, string][] = [["Nueva interacción", "interaccion"], ["Nueva cuenta", "cuenta"]];
  return (
    <div style={{ position: "fixed", right: 24, bottom: 24, zIndex: 80, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
      {abierto && (
        <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "var(--r-card)", boxShadow: "0 8px 24px rgba(11,29,58,.15)", padding: 6, width: 200 }}>
          {opciones.map(([l, que]) => (
            <button key={que} onClick={() => { setAbierto(false); capturar(que); }} className="fila-hover" style={{ display: "block", width: "100%", padding: "8px 10px", borderRadius: "var(--r-control)", fontSize: 13 }}>{l}</button>
          ))}
        </div>
      )}
      <button onClick={() => setAbierto((o) => !o)} aria-label="Capturar (C)" title="Capturar — C"
        style={{ width: 48, height: 48, borderRadius: "50%", background: "var(--sky)", color: "#fff", fontSize: 22, lineHeight: 1, boxShadow: "0 4px 14px rgba(59,130,246,.4)", display: "flex", alignItems: "center", justifyContent: "center" }}>+</button>
    </div>
  );
}

function HojaAtajos({ cerrar }: { cerrar: () => void }) {
  const filas: [string, string][] = [
    ["⌘K", "buscar / comandos"], ["C", "capturar"], ["G H", "ir a Hoy"], ["G C", "ir a Cuentas"], ["G D", "ir a Descubrimiento"],
    ["⌘\\", "colapsar barra"], ["J / K", "navegar lista"], ["V / X", "verificar / descartar"], ["E", "editar y verificar"], ["⌘Z", "deshacer"], ["?", "esta hoja"],
  ];
  return (
    <div className="velo" onClick={cerrar}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 380 }}>
        <h2 className="subtitulo" style={{ marginBottom: 14 }}>Atajos</h2>
        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "8px 16px", alignItems: "center" }}>
          {filas.map(([k, d]) => [<Kbd key={k}>{k}</Kbd>, <span key={k + "d"} style={{ fontSize: 13, color: "var(--muted)" }}>{d}</span>])}
        </div>
      </div>
    </div>
  );
}
